import React, { useState, useEffect } from 'react';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Textarea } from './ui/textarea';
import { Input } from './ui/input';
import { toast } from 'sonner';
import { 
  Star, Users, Trophy, AlertTriangle, CheckCircle, Clock, Plus,
  ArrowUp, ArrowDown, Minus, Eye, XCircle, TrendingUp, Sparkles, Send, Pencil, Check, Trash2
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

// Star Rating Display
const StarDisplay = ({ value, showNumber = true }) => {
  const fullStars = Math.floor(value || 0);
  const hasHalf = (value || 0) - fullStars >= 0.5;
  
  return (
    <div className="flex items-center gap-1">
      <div className="flex">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`w-4 h-4 ${
              star <= fullStars
                ? 'fill-amber-400 text-amber-400'
                : star === fullStars + 1 && hasHalf
                ? 'fill-amber-200 text-amber-400'
                : 'fill-gray-200 text-gray-300'
            }`}
          />
        ))}
      </div>
      {showNumber && <span className="text-sm ml-1">{(value || 0).toFixed(1)}</span>}
    </div>
  );
};

// Score Badge with color coding
const ScoreBadge = ({ score, size = 'md' }) => {
  if (!score && score !== 0) return <span className="text-muted-foreground">-</span>;
  
  const getColor = (s) => {
    if (s >= 4) return 'bg-emerald-100 text-emerald-700 border-emerald-300';
    if (s >= 3) return 'bg-amber-100 text-amber-700 border-amber-300';
    return 'bg-red-100 text-red-700 border-red-300';
  };
  
  const sizeClasses = {
    sm: 'text-xs px-1.5 py-0.5',
    md: 'text-sm px-2 py-1',
    lg: 'text-lg px-3 py-1.5 font-bold'
  };
  
  return (
    <span className={`rounded-md border ${getColor(score)} ${sizeClasses[size]}`}>
      {score.toFixed(1)}
    </span>
  );
};

// Trend Arrow
const TrendArrow = ({ current, previous }) => {
  if (!previous) return <Minus className="w-4 h-4 text-gray-400" />;
  
  const diff = current - previous;
  if (diff > 0.1) return <ArrowUp className="w-4 h-4 text-emerald-500" />;
  if (diff < -0.1) return <ArrowDown className="w-4 h-4 text-red-500" />;
  return <Minus className="w-4 h-4 text-gray-400" />;
};

// Employee Detail Modal
const EmployeeDetailModal = ({ isOpen, onClose, employeeId, roundId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    if (isOpen && employeeId && roundId) {
      loadDetails();
    }
  }, [isOpen, employeeId, roundId]);
  
  const loadDetails = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/peer-review/employee/${employeeId}/details?round_id=${roundId}`);
      setData(res.data);
    } catch (err) {
      console.error('Error loading details:', err);
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Detalii Review</DialogTitle>
          <DialogDescription>
            Evaluări primite de la colegi
          </DialogDescription>
        </DialogHeader>
        
        {loading ? (
          <div className="py-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
          </div>
        ) : data?.reviews?.length > 0 ? (
          <div className="space-y-4">
            {/* Summary */}
            {data.scores?.average_scores && (
              <div className="grid grid-cols-5 gap-2 text-center p-4 bg-slate-50 rounded-lg">
                <div>
                  <p className="text-xs text-muted-foreground">Colaborare</p>
                  <ScoreBadge score={data.scores.average_scores.collaboration} />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Calitate</p>
                  <ScoreBadge score={data.scores.average_scores.quality} />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Deadline</p>
                  <ScoreBadge score={data.scores.average_scores.deadlines} />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Comunicare</p>
                  <ScoreBadge score={data.scores.average_scores.communication} />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground font-medium">TOTAL</p>
                  <ScoreBadge score={data.scores.overall_average} size="lg" />
                </div>
              </div>
            )}
            
            {/* Individual reviews table */}
            <table className="w-full text-sm">
              <thead className="bg-slate-100">
                <tr>
                  <th className="p-2 text-left">Reviewer</th>
                  <th className="p-2 text-center">Colab.</th>
                  <th className="p-2 text-center">Calit.</th>
                  <th className="p-2 text-center">Deadline</th>
                  <th className="p-2 text-center">Comun.</th>
                  <th className="p-2 text-center">Medie</th>
                  <th className="p-2 text-left">Comentariu</th>
                </tr>
              </thead>
              <tbody>
                {data.reviews.map((review) => (
                  <tr key={review.id} className="border-b">
                    <td className="p-2 font-medium">{review.reviewer_name}</td>
                    <td className="p-2 text-center">
                      <StarDisplay value={review.scores.collaboration} showNumber={false} />
                    </td>
                    <td className="p-2 text-center">
                      <StarDisplay value={review.scores.quality} showNumber={false} />
                    </td>
                    <td className="p-2 text-center">
                      <StarDisplay value={review.scores.deadlines} showNumber={false} />
                    </td>
                    <td className="p-2 text-center">
                      <StarDisplay value={review.scores.communication} showNumber={false} />
                    </td>
                    <td className="p-2 text-center">
                      <ScoreBadge score={review.average} size="sm" />
                    </td>
                    <td className="p-2 text-muted-foreground text-xs max-w-[300px]">
                      {review.comment || '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-center text-muted-foreground py-8">
            Nu există evaluări pentru acest angajat.
          </p>
        )}
      </DialogContent>
    </Dialog>
  );
};

// AI Summary Modal - Generate, Edit, Approve
const AISummaryModal = ({ isOpen, onClose, employeeId, employeeName, roundId, onApproved }) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [summaryText, setSummaryText] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [saving, setSaving] = useState(false);
  const [existingApproval, setExistingApproval] = useState(null);
  
  useEffect(() => {
    if (isOpen && employeeId && roundId) {
      checkExisting();
    }
  }, [isOpen, employeeId, roundId]);
  
  const checkExisting = async () => {
    try {
      const res = await api.get(`/peer-review/approved-summary/${employeeId}?round_id=${roundId}`);
      if (res.data.found) {
        setExistingApproval(res.data.summary);
        setSummaryText(res.data.summary.summary_text);
        setSuggestions(res.data.summary.suggestions || []);
      } else {
        setExistingApproval(null);
      }
    } catch (err) {
      console.error('Error checking existing:', err);
    }
  };
  
  const handleGenerate = async () => {
    setLoading(true);
    try {
      const res = await api.post(`/peer-review/generate-summary/${employeeId}?round_id=${roundId}`);
      setData(res.data);
      setSummaryText(res.data.ai_summary || '');
      setSuggestions(res.data.ai_suggestions || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Eroare la generarea rezumatului');
    } finally {
      setLoading(false);
    }
  };
  
  const handleApprove = async () => {
    setSaving(true);
    try {
      await api.post('/peer-review/approve-summary', {
        employee_id: employeeId,
        round_id: roundId,
        summary_text: summaryText,
        suggestions: suggestions.filter(s => s.trim()),
        approved: true
      });
      toast.success(`Rezumatul pentru ${employeeName} a fost aprobat!`);
      onApproved?.();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Eroare la salvare');
    } finally {
      setSaving(false);
    }
  };
  
  const updateSuggestion = (index, value) => {
    const updated = [...suggestions];
    updated[index] = value;
    setSuggestions(updated);
  };
  
  const removeSuggestion = (index) => {
    setSuggestions(suggestions.filter((_, i) => i !== index));
  };
  
  const addSuggestion = () => {
    setSuggestions([...suggestions, '']);
  };
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-amber-500" />
            Rezumat AI - {employeeName}
          </DialogTitle>
          <DialogDescription>
            Generează, editează și aprobă feedback-ul pentru angajat
          </DialogDescription>
        </DialogHeader>
        
        {/* Existing approved summary notice */}
        {existingApproval && !data && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-sm text-emerald-700 flex items-center gap-2">
            <CheckCircle className="w-4 h-4 flex-shrink-0" />
            Rezumat aprobat anterior. Poți edita sau regenera.
          </div>
        )}
        
        {/* Scores overview from data */}
        {data && (
          <div className="bg-slate-50 rounded-lg p-4 space-y-2">
            <p className="text-sm font-medium">Scoruri ({data.total_reviews} evaluări):</p>
            <div className="grid grid-cols-5 gap-2 text-center text-xs">
              <div>
                <p className="text-muted-foreground">Colaborare</p>
                <ScoreBadge score={data.scores?.collaboration} size="sm" />
              </div>
              <div>
                <p className="text-muted-foreground">Calitate</p>
                <ScoreBadge score={data.scores?.quality} size="sm" />
              </div>
              <div>
                <p className="text-muted-foreground">Deadline</p>
                <ScoreBadge score={data.scores?.deadlines} size="sm" />
              </div>
              <div>
                <p className="text-muted-foreground">Comunicare</p>
                <ScoreBadge score={data.scores?.communication} size="sm" />
              </div>
              <div>
                <p className="text-muted-foreground font-medium">TOTAL</p>
                <ScoreBadge score={data.scores?.overall} size="md" />
              </div>
            </div>
            {data.comments?.length > 0 && (
              <div className="mt-3 pt-3 border-t">
                <p className="text-sm font-medium mb-1">Comentarii colegi:</p>
                {data.comments.map((c, i) => (
                  <p key={i} className="text-xs text-muted-foreground italic ml-2">"{c}"</p>
                ))}
              </div>
            )}
          </div>
        )}
        
        {/* Generate button */}
        {!data && !existingApproval && (
          <div className="text-center py-6">
            <Button 
              onClick={handleGenerate} 
              disabled={loading}
              className="bg-amber-500 hover:bg-amber-600"
              data-testid="generate-ai-summary-btn"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                  Se generează...
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4 mr-2" />
                  Generează Rezumat AI
                </>
              )}
            </Button>
          </div>
        )}
        
        {/* Regenerate button */}
        {(data || existingApproval) && (
          <div className="flex justify-end">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleGenerate} 
              disabled={loading}
            >
              {loading ? 'Se regenerează...' : 'Regenerează AI'}
            </Button>
          </div>
        )}
        
        {/* Editable summary */}
        {(summaryText || suggestions.length > 0) && (
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium flex items-center gap-1 mb-2">
                <Pencil className="w-3.5 h-3.5" />
                Rezumat (editabil)
              </label>
              <Textarea
                value={summaryText}
                onChange={(e) => setSummaryText(e.target.value)}
                rows={3}
                placeholder="Rezumatul feedback-ului..."
              />
            </div>
            
            <div>
              <label className="text-sm font-medium flex items-center gap-1 mb-2">
                <TrendingUp className="w-3.5 h-3.5" />
                Sugestii de îmbunătățire (editabile)
              </label>
              <div className="space-y-2">
                {suggestions.map((s, i) => (
                  <div key={i} className="flex gap-2">
                    <Input
                      value={s}
                      onChange={(e) => updateSuggestion(i, e.target.value)}
                      placeholder={`Sugestie ${i + 1}...`}
                    />
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => removeSuggestion(i)}
                      className="text-red-400 hover:text-red-600 px-2"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
                <Button variant="outline" size="sm" onClick={addSuggestion}>
                  <Plus className="w-3 h-3 mr-1" /> Adaugă sugestie
                </Button>
              </div>
            </div>
          </div>
        )}
        
        {/* Approve button */}
        {(summaryText || suggestions.length > 0) && (
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button variant="outline" onClick={onClose}>Anulează</Button>
            <Button 
              onClick={handleApprove} 
              disabled={saving || !summaryText.trim()}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="approve-summary-btn"
            >
              {saving ? 'Se salvează...' : (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  Aprobă și Trimite
                </>
              )}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

// Main Admin Peer Review Component
export const AdminPeerReview = () => {
  const [loading, setLoading] = useState(true);
  const [activeRound, setActiveRound] = useState(null);
  const [rounds, setRounds] = useState([]);
  const [selectedRound, setSelectedRound] = useState(null);
  const [summary, setSummary] = useState(null);
  const [history, setHistory] = useState([]);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [creating, setCreating] = useState(false);
  const [closing, setClosing] = useState(false);
  const [showAISummary, setShowAISummary] = useState(false);
  const [aiEmployee, setAiEmployee] = useState(null);
  
  useEffect(() => {
    loadData();
  }, []);
  
  useEffect(() => {
    if (selectedRound) {
      loadSummary(selectedRound);
    }
  }, [selectedRound]);
  
  const loadData = async () => {
    setLoading(true);
    try {
      const [activeRes, roundsRes, historyRes] = await Promise.all([
        api.get('/peer-review/active-round'),
        api.get('/peer-review/rounds'),
        api.get('/peer-review/history?weeks=12')
      ]);
      
      setActiveRound(activeRes.data.active ? activeRes.data.round : null);
      setRounds(roundsRes.data || []);
      setHistory(historyRes.data.history || []);
      
      // Select active round or most recent
      if (activeRes.data.active) {
        setSelectedRound(activeRes.data.round.id);
      } else if (roundsRes.data?.length > 0) {
        setSelectedRound(roundsRes.data[0].id);
      }
    } catch (err) {
      console.error('Error loading peer review data:', err);
    } finally {
      setLoading(false);
    }
  };
  
  const loadSummary = async (roundId) => {
    try {
      const res = await api.get(`/peer-review/rounds/${roundId}/summary`);
      setSummary(res.data);
    } catch (err) {
      console.error('Error loading summary:', err);
    }
  };
  
  const handleCreateRound = async () => {
    setCreating(true);
    try {
      await api.post('/peer-review/rounds');
      toast.success('Runda de evaluare a fost pornită!');
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Eroare la crearea rundei');
    } finally {
      setCreating(false);
    }
  };
  
  const handleCloseRound = async () => {
    if (!activeRound) return;
    
    setClosing(true);
    try {
      await api.post(`/peer-review/rounds/${activeRound.id}/close`);
      toast.success('Runda a fost închisă!');
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Eroare la închiderea rundei');
    } finally {
      setClosing(false);
    }
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  // Prepare chart data
  const chartData = history.map(h => ({
    week: h.week_label?.split(' ')[0] || '',
    team: h.team_average || 0,
    ...h.scores_by_employee
  }));
  
  const COLORS = ['#10B981', '#3B82F6', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899'];
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <Star className="w-5 h-5 text-amber-500" />
            Evaluare Colaborare Echipă
          </h2>
          <p className="text-sm text-muted-foreground">
            Review peer-to-peer săptămânal
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={selectedRound || ''} onValueChange={setSelectedRound}>
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="Selectează săptămâna" />
            </SelectTrigger>
            <SelectContent>
              {rounds.map(r => (
                <SelectItem key={r.id} value={r.id}>
                  {r.week_label} {r.status === 'active' && '(activ)'}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          {activeRound ? (
            <Button 
              variant="outline" 
              onClick={handleCloseRound}
              disabled={closing}
              className="border-red-300 text-red-600 hover:bg-red-50"
            >
              <XCircle className="w-4 h-4 mr-2" />
              {closing ? 'Se închide...' : 'Închide Runda'}
            </Button>
          ) : (
            <Button 
              onClick={handleCreateRound}
              disabled={creating}
              className="bg-orange-500 hover:bg-orange-600"
            >
              <Plus className="w-4 h-4 mr-2" />
              {creating ? 'Se creează...' : 'Pornește Runda Nouă'}
            </Button>
          )}
        </div>
      </div>
      
      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Scor Mediu Echipă</p>
                  <ScoreBadge score={summary.team_average_score} size="lg" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Review-uri Completate</p>
                  <p className="text-xl font-bold">
                    {summary.total_submitted_reviews} / {summary.total_expected_reviews}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                  <Trophy className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Cel Mai Colaborativ</p>
                  {summary.most_collaborative ? (
                    <p className="font-bold truncate">
                      {summary.most_collaborative.employee_name}
                      <span className="text-emerald-600 ml-1">
                        ({summary.most_collaborative.overall_score?.toFixed(1)})
                      </span>
                    </p>
                  ) : <p className="text-muted-foreground">-</p>}
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className={summary.needs_attention_count > 0 ? 'border-red-200 bg-red-50' : ''}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                  summary.needs_attention_count > 0 ? 'bg-red-100' : 'bg-slate-100'
                }`}>
                  <AlertTriangle className={`w-5 h-5 ${
                    summary.needs_attention_count > 0 ? 'text-red-600' : 'text-slate-400'
                  }`} />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Necesită Atenție</p>
                  <p className={`text-xl font-bold ${
                    summary.needs_attention_count > 0 ? 'text-red-600' : ''
                  }`}>
                    {summary.needs_attention_count}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
      
      {/* Team Scores Table */}
      {summary?.employee_scores && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Scoruri Echipă</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b">
                  <tr>
                    <th className="p-3 text-left font-medium">Nume</th>
                    <th className="p-3 text-center font-medium">Colaborare</th>
                    <th className="p-3 text-center font-medium">Calitate</th>
                    <th className="p-3 text-center font-medium">Deadline</th>
                    <th className="p-3 text-center font-medium">Comunicare</th>
                    <th className="p-3 text-center font-medium">Scor General</th>
                    <th className="p-3 text-center font-medium">Acțiuni</th>
                  </tr>
                </thead>
                <tbody>
                  {summary.employee_scores.map((emp) => (
                    <tr key={emp.employee_id} className="border-b hover:bg-slate-50">
                      <td className="p-3 font-medium">{emp.employee_name}</td>
                      <td className="p-3 text-center">
                        {emp.scores ? <StarDisplay value={emp.scores.collaboration} /> : '-'}
                      </td>
                      <td className="p-3 text-center">
                        {emp.scores ? <StarDisplay value={emp.scores.quality} /> : '-'}
                      </td>
                      <td className="p-3 text-center">
                        {emp.scores ? <StarDisplay value={emp.scores.deadlines} /> : '-'}
                      </td>
                      <td className="p-3 text-center">
                        {emp.scores ? <StarDisplay value={emp.scores.communication} /> : '-'}
                      </td>
                      <td className="p-3 text-center">
                        <ScoreBadge score={emp.overall_score} size="lg" />
                      </td>
                      <td className="p-3 text-center">
                        <div className="flex items-center justify-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setSelectedEmployee(emp.employee_id);
                              setShowDetailModal(true);
                            }}
                            disabled={!emp.scores}
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            Detalii
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setAiEmployee({ id: emp.employee_id, name: emp.employee_name });
                              setShowAISummary(true);
                            }}
                            disabled={!emp.scores}
                            className="text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                            data-testid={`ai-summary-btn-${emp.employee_id}`}
                          >
                            <Sparkles className="w-4 h-4 mr-1" />
                            Rezumat AI
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Historical Chart */}
      {chartData.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <TrendingUp className="w-4 h-4" />
              Istoric Scoruri (ultimele 12 săptămâni)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="week" />
                  <YAxis domain={[1, 5]} />
                  <Tooltip />
                  <Legend />
                  <Line 
                    type="monotone" 
                    dataKey="team" 
                    stroke="#6B7280" 
                    strokeWidth={2}
                    name="Media Echipă"
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Employee Detail Modal */}
      <EmployeeDetailModal
        isOpen={showDetailModal}
        onClose={() => {
          setShowDetailModal(false);
          setSelectedEmployee(null);
        }}
        employeeId={selectedEmployee}
        roundId={selectedRound}
      />
      
      {/* AI Summary Modal */}
      <AISummaryModal
        isOpen={showAISummary}
        onClose={() => {
          setShowAISummary(false);
          setAiEmployee(null);
        }}
        employeeId={aiEmployee?.id}
        employeeName={aiEmployee?.name}
        roundId={selectedRound}
        onApproved={() => loadSummary(selectedRound)}
      />
    </div>
  );
};

export default AdminPeerReview;
